import type { AnnualTimetableCell, Grade } from '../types/domain';
import { buildAnnualClassEntries, getSlotLimitForDay } from './annualTimetable';
import { toISODate } from './date';
import {
  buildTodayClassLessonAlarmTargets,
  isClassLessonAlarmWeekdayKey,
  resolveAlarmAcademicYearTerm,
  resolveAlarmDayKeys,
  resolveClassScheduleSettingsFromLocalCaches,
  type ClassLessonAlarmTarget,
} from './classLessonAlarmSchedule';
import {
  classLessonAlarmOffsetLabel,
  classLessonAlarmSoundLabel,
  type ClassLessonAlarmOffset,
  type ClassLessonAlarmSettings,
  type ClassLessonAlarmSoundKind,
} from './classLessonAlarmSettings';

export interface ClassLessonAlarmDiagnosis {
  nowLabel: string;
  weekdayLabel: string;
  isWeekend: boolean;
  scheduleCacheFound: boolean;
  classEntryCount: number;
  todayWeekdayMaxHint: string;
  masterEnabled: boolean;
  academicYear: number;
  termNumber: 1 | 2;
  targets: ClassLessonAlarmTarget[];
  firePlan: Array<{
    startTime: string;
    channel: 'regular' | 'dedicated';
    channelLabel: string;
    offset: ClassLessonAlarmOffset;
    offsetLabel: string;
    soundKind: ClassLessonAlarmSoundKind;
    soundLabel: string;
    gradeId: string;
    gradeLabel: string;
    slotIndex: number;
    periodLabel: string;
    hasLesson: boolean;
    willRing: boolean;
  }>;
  blockers: string[];
  tips: string[];
}

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/** 수업 알람이 왜 안 울리는지 점검용 진단 */
export function diagnoseClassLessonAlarm(params: {
  workspaceId: string;
  grades: Grade[];
  academicYear?: number;
  termNumber?: 1 | 2;
  settings: ClassLessonAlarmSettings;
  now?: Date;
  /** ensure에서 확보한 연간 셀(있으면 LS 재조회 대신 사용) */
  cellsByKey?: Record<string, AnnualTimetableCell>;
}): ClassLessonAlarmDiagnosis {
  const now = params.now ?? new Date();
  const nowLabel = `${toISODate(now)} ${String(now.getHours()).padStart(2, '0')}:${String(
    now.getMinutes()
  ).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  const weekdayLabel = WEEKDAY_LABELS[now.getDay()] ?? '?';
  const { weekdayKey: dayKey } = resolveAlarmDayKeys(now);
  const weekdayKey = isClassLessonAlarmWeekdayKey(dayKey) ? dayKey : null;
  const isSunday = weekdayKey == null;
  const masterEnabled = params.settings.masterEnabled !== false;
  const sid = params.workspaceId.trim();
  const resolved = resolveAlarmAcademicYearTerm(sid, now, null);
  const academicYear = params.academicYear ?? resolved.academicYear;
  const termNumber = params.termNumber ?? resolved.termNumber;
  const blockers: string[] = [];
  const tips: string[] = [
    '알람 블록은 수업시간 설정의 「수업」 행(+시작시각) 기준으로 생성됩니다.',
    '활성 여부는 대시보드와 같이 연간시간표(오늘) 셀을 기준으로 합니다.',
  ];

  if (!masterEnabled) {
    blockers.push('수업 알람이 꺼져 있습니다. 왼쪽 상단에서 켜면 오늘 알람 블록이 생성됩니다.');
  }

  if (isSunday) {
    blockers.push('해당 날짜는 일요일이라 수업 알람 대상이 없습니다. (월~토만)');
  }

  const schedule =
    sid && masterEnabled
      ? resolveClassScheduleSettingsFromLocalCaches({
          workspaceId: sid,
          academicYear,
          termNumber,
          gradeIds: params.grades.map((g) => g.id),
        })
      : null;
  const scheduleCacheFound = !!schedule;
  if (masterEnabled && !scheduleCacheFound) {
    blockers.push('수업시간 데이터를 불러오는 중이거나 아직 없습니다.');
  }

  const entries = schedule ? buildAnnualClassEntries(schedule, params.grades) : [];
  if (masterEnabled && scheduleCacheFound && entries.length === 0) {
    blockers.push(
      '클래스(학년)가 수업시간 섹션에 연결되어 있지 않습니다. 클래스 등록 및 수업시간 섹션의 학년 배정을 확인하세요.'
    );
  }

  let todayWeekdayMaxHint = '-';
  if (schedule && weekdayKey && entries.length) {
    const hints = entries.map((e) => {
      const max = getSlotLimitForDay(e.slotLimitByDay, weekdayKey);
      return `${e.label}: 오늘 시수상한 ${max}`;
    });
    todayWeekdayMaxHint = hints.join(' · ');
  }

  const targets =
    masterEnabled && scheduleCacheFound && !isSunday
      ? buildTodayClassLessonAlarmTargets({
          workspaceId: sid,
          grades: params.grades,
          academicYear,
          termNumber,
          settings: params.settings,
          now,
          dedupe: false,
          cellsByKey: params.cellsByKey,
        })
      : [];

  if (masterEnabled && !isSunday && scheduleCacheFound && entries.length > 0 && targets.length === 0) {
    blockers.push(
      '수업 행(+시작시각)이 없어 알람 블록을 만들 수 없습니다. 수업시간 설정에서 수업 행과 시작 시각을 확인하세요.'
    );
  }

  const firePlan = targets.map((t) => ({
    startTime: t.time,
    channel: t.channel,
    channelLabel: t.channel === 'dedicated' ? '전담' : '일반',
    offset: t.offset,
    offsetLabel: classLessonAlarmOffsetLabel(t.offset),
    soundKind: t.soundKind,
    soundLabel: classLessonAlarmSoundLabel(t.soundKind),
    gradeId: t.gradeId,
    gradeLabel: t.gradeLabel,
    slotIndex: t.slotIndex,
    periodLabel: t.periodLabel,
    hasLesson: t.hasLesson,
    willRing: t.willRing,
  }));

  return {
    nowLabel,
    weekdayLabel,
    isWeekend: isSunday,
    scheduleCacheFound,
    classEntryCount: entries.length,
    todayWeekdayMaxHint,
    masterEnabled,
    academicYear,
    termNumber,
    targets,
    firePlan,
    blockers,
    tips,
  };
}
